import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  BankOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  FileImageOutlined,
  FileOutlined,
  GlobalOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import useAppConfig from "hooks/access-control/useAppConfig";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import { useNavigate, useSearchParams } from "react-router-dom";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import {
  formatDate,
  organizationName,
  readable,
  statusColor,
} from "workspaces/workspaceUtils";
import { OLIVEEB_COLORS } from "workspaces/colors";
import "./OrganizationsPage.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);
const pageTotal = (data, fallback = 0) =>
  Number(data?.totalElements ?? data?.total ?? fallback) || 0;

const trim = (value) => String(value || "").trim();

const STATUS_OPTIONS = ["DRAFT", "ACTIVE", "INACTIVE", "SUSPENDED"];
const USER_ROLE_OPTIONS = ["OWNER", "ADMIN", "MANAGER", "MEMBER"];
const USER_STATUS_OPTIONS = ["ACTIVE", "INVITED", "INACTIVE"];

const contactText = (entity) => {
  const contact = entity?.contactInformation || {};
  return [
    contact.primaryEmail || entity?.email,
    contact.primaryPhone || entity?.phoneNumber || entity?.phone,
    contact.websiteUrl || entity?.websiteUrl || entity?.website,
  ]
    .filter(Boolean)
    .join(" • ");
};

const addressText = (source) => {
  const location = source?.locationInformation || source || {};
  return [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.county,
    location.state,
    location.zipCode,
    location.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const serviceLabel = (service) =>
  service?.name || service?.label || readable(service?.code, "Service");

const locationTypeLabel = (value) =>
  value?.label || value?.name || readable(value?.code || value, "Location type");

const orgInitial = (entity) =>
  organizationName(entity, "O").charAt(0).toUpperCase();

const mapUrl = (location) => {
  const query = addressText(location) || location?.name || "Care facility location";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

const extractOrganizationLocation = (components = [], placeId = "", geometry = {}) => {
  const safeComponents = Array.isArray(components) ? components : [];
  const get = (type, useShort = false) => {
    const component = safeComponents.find((item) => Array.isArray(item.types) && item.types.includes(type));
    return useShort ? component?.short_name || "" : component?.long_name || "";
  };
  const streetNumber = get("street_number");
  const route = get("route");

  return {
    addressLine1: [streetNumber, route].filter(Boolean).join(" "),
    addressLine2: "",
    city: get("locality") || get("sublocality") || get("administrative_area_level_2"),
    county: get("administrative_area_level_2"),
    state: get("administrative_area_level_1", true),
    country: get("country", true) || get("country") || "USA",
    zipCode: get("postal_code"),
    latitude: typeof geometry?.location?.lat === "number" ? geometry.location.lat : null,
    longitude: typeof geometry?.location?.lng === "number" ? geometry.location.lng : null,
    placeId: placeId || null,
  };
};

const userLabel = (user) => {
  const profile = user?.user || user?.profile || {};
  return (
    [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ") ||
    profile.fullName ||
    user?.fullName ||
    user?.name ||
    user?.email ||
    profile.email ||
    "Care facility user"
  );
};

const userEmail = (user) =>
  user?.email || user?.user?.email || user?.profile?.email || "";

const fileIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^(https?:|data:|blob:)/i.test(value) ? "" : value;
  return value.fileId || value.id || value.uuid || value.file?.fileId || value.file?.id || value.file?.uuid || "";
};

const csvToList = (value) =>
  String(value || "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const listToCsv = (value) =>
  Array.isArray(value) ? value.map(fileIdValue).filter(Boolean).join(", ") : fileIdValue(value);

const keepExistingUrlOnlyFile = (value) => Boolean(fileUrlValue(value) && !fileIdValue(value));

const compactPatchObject = (value) =>
  Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined));

const fileUrlValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^(https?:|data:|blob:)/i.test(value) ? value : "";
  return (
    value.signedUrl ||
    value.signedURL ||
    value.signed_url ||
    value.downloadUrl ||
    value.downloadURL ||
    value.download_url ||
    value.previewUrl ||
    value.previewURL ||
    value.preview_url ||
    value.publicUrl ||
    value.publicURL ||
    value.public_url ||
    value.fileUrl ||
    value.fileURL ||
    value.file_url ||
    value.mediaUrl ||
    value.mediaURL ||
    value.media_url ||
    value.url ||
    value.uri ||
    value.href ||
    value.file?.signedUrl ||
    value.file?.signedURL ||
    value.file?.signed_url ||
    value.file?.downloadUrl ||
    value.file?.downloadURL ||
    value.file?.download_url ||
    value.file?.previewUrl ||
    value.file?.publicUrl ||
    value.file?.fileUrl ||
    value.file?.url ||
    value.file?.uri ||
    ""
  );
};

const firstFileUrl = (...values) => values.map(fileUrlValue).find(Boolean) || "";

const organizationFileItems = (entity = {}) => {
  const baseItems = [
    {
      key: "certificate",
      label: "Certificate",
      value: entity.certificate || entity.certificateFile || entity.certificateDocument,
      url: firstFileUrl(entity.certificate, entity.certificateUrl, entity.certificateUri, entity.certificateSignedUrl, entity.certificateDownloadUrl),
      kind: "document",
    },
    {
      key: "logo",
      label: "Logo",
      value: entity.logo || entity.logoFile,
      url: firstFileUrl(entity.logo, entity.logoUrl, entity.logoUri, entity.logoSignedUrl, entity.logoDownloadUrl),
      kind: "image",
    },
    {
      key: "profilePhoto",
      label: "Profile photo",
      value: entity.profilePhoto || entity.profilePhotoFile,
      url: firstFileUrl(entity.profilePhoto, entity.profilePhotoUrl, entity.profilePhotoUri, entity.profilePhotoSignedUrl, entity.profilePhotoDownloadUrl),
      kind: "image",
    },
  ];
  const otherDocs = [entity.otherDocs, entity.otherDocuments, entity.documents]
    .find(Array.isArray) || [];
  return [
    ...baseItems,
    ...otherDocs.map((doc, index) => ({
      key: `other-${index}`,
      label: `Document ${index + 1}`,
      value: doc,
      kind: "document",
    })),
  ]
    .map((item) => ({
      ...item,
      fileId: fileIdValue(item.value),
      url: item.url || fileUrlValue(item.value),
    }))
    .filter((item) => item.fileId || item.url);
};

const organizationInitialValues = (entity = {}) => {
  const contact = entity.contactInformation || {};
  const location = entity.locationInformation || {};
  const services = entity.organizationOfferings || entity.offerings || entity.services || [];
  return {
    name: organizationName(entity, ""),
    description: entity.description || "",
    ein: entity.ein || "",
    certificate: fileIdValue(entity.certificate),
    logo: fileIdValue(entity.logo),
    profilePhoto: fileIdValue(entity.profilePhoto),
    otherDocs: listToCsv(entity.otherDocs),
    status: entity.status || "PENDING_REVIEW",
    organizationLocationTypeId:
      entity.organizationLocationTypeId ||
      entity.businessLocationType?.id ||
      entity.organizationLocationType?.id ||
      undefined,
    primaryEmail: contact.primaryEmail || entity.email || "",
    secondaryEmail: contact.secondaryEmail || "",
    primaryPhone: contact.primaryPhone || entity.phoneNumber || entity.phone || "",
    secondaryPhone: contact.secondaryPhone || "",
    websiteUrl: contact.websiteUrl || entity.websiteUrl || entity.website || "",
    contactPersonName: contact.contactPersonName || "",
    contactPersonTitle: contact.contactPersonTitle || "",
    addressLine1: location.addressLine1 || "",
    addressLine2: location.addressLine2 || "",
    city: location.city || "",
    county: location.county || "",
    state: location.state || "",
    zipCode: location.zipCode || "",
    country: location.country || "USA",
    serviceIds: services.map((service) => service.id).filter(Boolean),
  };
};

const organizationPayload = (values, existing = {}) => {
  const otherDocs = csvToList(values.otherDocs);
  const originalOtherDocs = Array.isArray(existing.otherDocs) ? existing.otherDocs : [];
  const fileValue = (field) => {
    const nextValue = trim(values[field]);
    if (nextValue) return nextValue;
    return keepExistingUrlOnlyFile(existing[field]) ? undefined : null;
  };

  return compactPatchObject({
    name: trim(values.name),
    description: trim(values.description),
    ein: trim(values.ein) || null,
    certificate: fileValue("certificate"),
    logo: fileValue("logo"),
    profilePhoto: fileValue("profilePhoto"),
    otherDocs:
      otherDocs.length > 0 || !originalOtherDocs.some(keepExistingUrlOnlyFile)
        ? otherDocs
        : undefined,
    status: values.status || "PENDING_REVIEW",
    organizationLocationTypeId: values.organizationLocationTypeId,
    contactInformation: {
    primaryEmail: trim(values.primaryEmail) || null,
    secondaryEmail: trim(values.secondaryEmail) || null,
    primaryPhone: trim(values.primaryPhone) || null,
    secondaryPhone: trim(values.secondaryPhone) || null,
    websiteUrl: trim(values.websiteUrl) || null,
    contactPersonName: trim(values.contactPersonName) || null,
    contactPersonTitle: trim(values.contactPersonTitle) || null,
    },
    locationInformation: {
    addressLine1: trim(values.addressLine1) || null,
    addressLine2: trim(values.addressLine2) || null,
    city: trim(values.city) || null,
    county: trim(values.county) || null,
    state: trim(values.state) || null,
    zipCode: trim(values.zipCode) || null,
    country: trim(values.country) || "USA",
    },
  });
};

const patchOrganizationProps = async (organizationId, payload, actorId) => {
  let nextEntity = null;
  for (const [name, value] of Object.entries(payload)) {
    if (value !== undefined) {
      const updated = await olreadiaService.organizations.updateProp(organizationId, { name, value }, actorId);
      if (updated) nextEntity = updated;
    }
  }
  return nextEntity;
};

const locationInitialValues = (location = {}) => {
  const contact = location.contactInformation || {};
  const info = location.locationInformation || {};
  const services = location.organizationOfferings || location.offerings || location.services || [];
  return {
    name: location.name || "",
    status: location.status || "ACTIVE",
    organizationLocationTypeId:
      location.organizationLocationTypeId ||
      location.businessLocationType?.id ||
      location.organizationLocationType?.id ||
      undefined,
    description: location.description || "",
    primaryEmail: contact.primaryEmail || "",
    secondaryEmail: contact.secondaryEmail || "",
    primaryPhone: contact.primaryPhone || "",
    secondaryPhone: contact.secondaryPhone || "",
    websiteUrl: contact.websiteUrl || "",
    contactPersonName: contact.contactPersonName || "",
    contactPersonTitle: contact.contactPersonTitle || "",
    addressLine1: info.addressLine1 || "",
    addressLine2: info.addressLine2 || "",
    city: info.city || "",
    county: info.county || "",
    state: info.state || "",
    zipCode: info.zipCode || "",
    country: info.country || "USA",
    serviceIds: services.map((service) => service.id).filter(Boolean),
  };
};

const locationPayload = (values) => ({
  name: trim(values.name),
  status: values.status || "ACTIVE",
  description: trim(values.description),
  organizationLocationTypeId: values.organizationLocationTypeId,
  contactInformation: {
    primaryEmail: trim(values.primaryEmail) || null,
    secondaryEmail: trim(values.secondaryEmail) || null,
    primaryPhone: trim(values.primaryPhone) || null,
    secondaryPhone: trim(values.secondaryPhone) || null,
    websiteUrl: trim(values.websiteUrl) || null,
    contactPersonName: trim(values.contactPersonName) || null,
    contactPersonTitle: trim(values.contactPersonTitle) || null,
  },
  locationInformation: {
    addressLine1: trim(values.addressLine1) || null,
    addressLine2: trim(values.addressLine2) || null,
    city: trim(values.city) || null,
    county: trim(values.county) || null,
    state: trim(values.state) || null,
    zipCode: trim(values.zipCode) || null,
    country: trim(values.country) || "USA",
  },
});

function MiniStat({ label, value }) {
  return (
    <div className="organizations-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DetailRow({ label, value, onEdit, editing }) {
  return (
    <div className="organizations-detail-row">
      <span>
        <Text className="organizations-detail-label">{label}</Text>
        <Text>{value || "-"}</Text>
      </span>
      {onEdit ? (
        <Button
          className="organizations-row-edit"
          size="small"
          type="text"
          icon={<EditOutlined />}
          loading={editing}
          onClick={onEdit}
        />
      ) : null}
    </div>
  );
}

function FileThumb({ item, url, loading, onPreview }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(url) && !imageFailed;

  return (
    <button type="button" className="organizations-file-thumb" onClick={onPreview}>
      <span className="organizations-file-thumb__media">
        {showImage ? (
          <img src={url} alt={item.label} onError={() => setImageFailed(true)} />
        ) : item.kind === "image" ? (
          <FileImageOutlined />
        ) : (
          <FileOutlined />
        )}
      </span>
      <span className="organizations-file-thumb__copy">
        <strong>{item.label}</strong>
        <span>{loading ? "Loading preview..." : "Preview"}</span>
      </span>
    </button>
  );
}

function AddressAutocomplete({ googleMapsApiKey }) {
  const form = Form.useFormInstance();
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const fetchPredictions = async (value) => {
    setQuery(value);
    if (!googleMapsApiKey || value.trim().length < 3) {
      setPredictions([]);
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(value)}&types=address&key=${googleMapsApiKey}`);
      const json = await response.json();
      setPredictions(
        Array.isArray(json.predictions)
          ? json.predictions.map((item) => ({ placeId: item.place_id, description: item.description }))
          : [],
      );
    } catch {
      setPredictions([]);
      message.error("Failed to search addresses.");
    } finally {
      setLoading(false);
    }
  };

  const selectPrediction = async (prediction) => {
    if (!googleMapsApiKey || !prediction?.placeId) return;
    try {
      setSelecting(true);
      const response = await fetch(`https://maps.googleapis.com/maps/api/place/details/json?place_id=${encodeURIComponent(prediction.placeId)}&fields=address_component,geometry,place_id&key=${googleMapsApiKey}`);
      const json = await response.json();
      const result = json.result || {};
      form.setFieldsValue(extractOrganizationLocation(result.address_components, result.place_id || prediction.placeId, result.geometry));
      setQuery(prediction.description || "");
      setPredictions([]);
    } catch {
      message.error("Failed to fetch address details.");
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="organizations-address-search">
      <Input.Search
        allowClear
        value={query}
        loading={loading || selecting}
        placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Address search unavailable"}
        disabled={!googleMapsApiKey}
        onChange={(event) => fetchPredictions(event.target.value)}
        onSearch={fetchPredictions}
      />
      {predictions.length ? (
        <div className="organizations-address-suggestions">
          {predictions.map((prediction) => (
            <button key={prediction.placeId} type="button" onClick={() => selectPrediction(prediction)}>
              <EnvironmentOutlined />
              <span>{prediction.description}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function OrganizationForm({
  form,
  locationTypeOptions,
  serviceOptions,
  saving,
  onCancel,
  onSubmit,
  editing,
}) {
  return (
    <Form form={form} layout="vertical" requiredMark={false} onFinish={onSubmit}>
      <div className="organizations-form-section">
        <Text className="organizations-section-title">Care Facility details</Text>
        <Form.Item
          name="name"
          label="Care Facility name"
          rules={[{ required: true, message: "Enter care facility name." }]}
        >
          <Input placeholder="Care Facility name" />
        </Form.Item>
        <Form.Item
          name="organizationLocationTypeId"
          label="Care Facility type"
          rules={[{ required: true, message: "Select care facility type." }]}
        >
          <Select
            showSearch
            placeholder="Select type"
            optionFilterProp="label"
            options={locationTypeOptions}
          />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="status" label="Status" className="organizations-compact-item">
            <Select options={STATUS_OPTIONS.map((value) => ({ value, label: readable(value) }))} />
          </Form.Item>
          <Form.Item name="ein" label="EIN" className="organizations-compact-item">
            <Input placeholder="Optional" />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Short care facility description" />
        </Form.Item>
      </div>

      <div className="organizations-form-section">
        <Text className="organizations-section-title">Care Facility offerings</Text>
        <Form.Item name="serviceIds" label="Services">
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Select care facility service offerings"
            optionFilterProp="label"
            options={serviceOptions}
          />
        </Form.Item>
      </div>

      <div className="organizations-form-section">
        <Text className="organizations-section-title">Contact</Text>
        <Space.Compact block>
          <Form.Item name="primaryEmail" label="Primary email" className="organizations-compact-item">
            <Input placeholder="facility@example.com" />
          </Form.Item>
          <Form.Item name="secondaryEmail" label="Secondary email" className="organizations-compact-item">
            <Input placeholder="Optional email" />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="primaryPhone" label="Primary phone" className="organizations-compact-item">
            <Input placeholder="+1..." />
          </Form.Item>
          <Form.Item name="secondaryPhone" label="Secondary phone" className="organizations-compact-item">
            <Input placeholder="Optional phone" />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="contactPersonName" label="Contact person" className="organizations-compact-item">
            <Input placeholder="Primary contact" />
          </Form.Item>
          <Form.Item name="contactPersonTitle" label="Contact title" className="organizations-compact-item">
            <Input placeholder="Manager, owner, admin" />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="websiteUrl" label="Website">
          <Input placeholder="https://..." />
        </Form.Item>
      </div>

      <div className="organizations-form-section">
        <Text className="organizations-section-title">Primary address</Text>
        <Form.Item name="addressLine1" label="Address line 1">
          <Input placeholder="Street address" />
        </Form.Item>
        <Form.Item name="addressLine2" label="Address line 2">
          <Input placeholder="Suite, unit, building" />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="city" label="City" className="organizations-compact-item">
            <Input />
          </Form.Item>
          <Form.Item name="county" label="County" className="organizations-compact-item">
            <Input />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="state" label="State" className="organizations-compact-item">
            <Input />
          </Form.Item>
          <Form.Item name="zipCode" label="Zip code" className="organizations-compact-item">
            <Input />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="country" label="Country" className="organizations-compact-item">
            <Input />
          </Form.Item>
        </Space.Compact>
      </div>

      <div className="organizations-form-section">
        <Text className="organizations-section-title">Files</Text>
        <Space.Compact block>
          <Form.Item name="certificate" label="Certificate file ID" className="organizations-compact-item">
            <Input placeholder="Uploaded certificate file ID" />
          </Form.Item>
          <Form.Item name="logo" label="Logo file ID" className="organizations-compact-item">
            <Input placeholder="Uploaded logo file ID" />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="profilePhoto" label="Profile photo file ID">
          <Input placeholder="Uploaded profile photo file ID" />
        </Form.Item>
        <Form.Item name="otherDocs" label="Other document file IDs">
          <TextArea rows={2} placeholder="Separate file IDs with commas or new lines" />
        </Form.Item>
      </div>

      <div className="organizations-drawer-actions">
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={saving}
          style={{ background: OLIVEEB_COLORS.oliveebGreen, borderColor: OLIVEEB_COLORS.oliveebGreen }}
        >
          {editing ? "Save Care Facility" : "Create Care Facility"}
        </Button>
      </div>
    </Form>
  );
}

export default function OrganizationsPage() {
  const { user } = useAuth();
  const { appConfig } = useAppConfig();
  const { uploadFile, uploading: fileUploading } = useFileUpload();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryWorkspaceId = searchParams.get("wid") || "";
  const actorId = user?.id || user?.userId || "";
  const [form] = Form.useForm();
  const [locationForm] = Form.useForm();
  const [userForm] = Form.useForm();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [organizationsPage, setOrganizationsPage] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [allOfferings, setAllOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editor, setEditor] = useState(null);
  const [locationEditor, setLocationEditor] = useState(null);
  const [locationDetails, setLocationDetails] = useState(null);
  const [userEditorOpen, setUserEditorOpen] = useState(false);
  const [offeringQuery, setOfferingQuery] = useState("");
  const [actionKey, setActionKey] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [propEditor, setPropEditor] = useState(null);
  const [updatingPropKey, setUpdatingPropKey] = useState("");

  const locationTypeOptions = useMemo(
    () =>
      locationTypes.map((type) => ({
        value: type.id,
        label: locationTypeLabel(type),
      })),
    [locationTypes],
  );

  const serviceOptions = useMemo(
    () =>
      allOfferings
        .filter((service) => service?.id)
        .map((service) => ({
          value: service.id,
          label: serviceLabel(service),
        })),
    [allOfferings],
  );

  const selectedLocations = selected?.businessLocations || selected?.organizationLocations || [];
  const selectedUsers = selected?.users || selected?.organizationUsers || [];
  const selectedServices =
    selected?.organizationOfferings || selected?.offerings || selected?.services || [];
  const selectedFileItems = useMemo(() => organizationFileItems(selected || {}), [selected]);
  const selectedOtherDocs = Array.isArray(selected?.otherDocs) ? selected.otherDocs : [];
  const locationDetailsServices = locationDetails?.services || locationDetails?.organizationOfferings || [];

  const selectedServiceIds = useMemo(
    () => new Set(selectedServices.map((service) => service.id).filter(Boolean)),
    [selectedServices],
  );
  const locationServiceOptions = useMemo(
    () => selectedServices.filter((service) => service?.id).map((service) => ({ value: service.id, label: serviceLabel(service) })),
    [selectedServices],
  );
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";

  const availableOfferings = useMemo(() => {
    const query = offeringQuery.trim().toLowerCase();
    return allOfferings
      .filter((service) => service?.id && !selectedServiceIds.has(service.id))
      .filter((service) => {
        if (!query) return true;
        return [service.name, service.code, service.category, service.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      })
      .slice(0, 12);
  }, [allOfferings, offeringQuery, selectedServiceIds]);

  const loadReferenceData = useCallback(async () => {
    if (!actorId) return;
    try {
      const [typePage, offeringPage] = await Promise.all([
        olreadiaService.businessLocationTypes.list({ active: true, size: 5000 }),
        olreadiaService.serviceOfferings.list({ active: true, size: 5000 }),
      ]);
      setLocationTypes(pageContent(typePage));
      setAllOfferings(pageContent(offeringPage));
    } catch (error) {
      message.error(error?.message || "Failed to load care facility options.");
    }
  }, [actorId]);

  const loadOrganizations = useCallback(async () => {
    if (!actorId) return;
    setLoading(true);
    try {
      const data = await olreadiaService.organizations.listForUser(actorId, {
        page: page - 1,
        size: pageSize,
      });
      const content = pageContent(data);
      setOrganizationsPage(data);
      setOrganizations(content);
    } catch (error) {
      message.error(error?.message || "Failed to load care facilities.");
      setOrganizations([]);
      setOrganizationsPage(null);
    } finally {
      setLoading(false);
    }
  }, [actorId, page, pageSize]);

  const loadSelected = useCallback(async (organizationId, open = true) => {
    if (!organizationId) return;
    setDetailLoading(true);
    if (open) setDetailOpen(true);
    try {
      const entity = await olreadiaService.organizations.get(organizationId, queryWorkspaceId ? { wid: queryWorkspaceId } : {});
      setSelected(entity || null);
    } catch (error) {
      message.error(error?.message || "Failed to load care facility details.");
    } finally {
      setDetailLoading(false);
    }
  }, [queryWorkspaceId]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const openFilePreview = async (item) => {
    setFilePreview({ ...item, url: item.url || "" });
  };

  const refreshAll = async () => {
    await Promise.all([loadOrganizations(), selected?.id ? loadSelected(selected.id, false) : Promise.resolve()]);
  };

  const openCreate = () => {
    setEditor({ mode: "create", organization: null });
    form.setFieldsValue(organizationInitialValues());
  };

  const closeEditor = () => {
    setEditor(null);
    form.resetFields();
  };

  const patchSelectedProp = async (name, value) => {
    if (!selected?.id) return null;
    const updated = await olreadiaService.organizations.updateProp(selected.id, { name, value }, actorId);
    if (updated?.id) setSelected(updated);
    await refreshAll();
    return updated;
  };

  const openSelectedPropEditor = ({ title, propName, value, multiline = false, options, buildValue }) => {
    setPropEditor({
      title,
      propName,
      value: value == null ? "" : String(value),
      multiline,
      options,
      buildValue,
    });
  };

  const saveSelectedProp = async () => {
    if (!propEditor?.propName) return;
    try {
      setSaving(true);
      setUpdatingPropKey(propEditor.propName);
      const rawValue = propEditor.value || "";
      const value = propEditor.buildValue ? propEditor.buildValue(rawValue) : trim(rawValue) || null;
      await patchSelectedProp(propEditor.propName, value);
      setPropEditor(null);
      message.success("Care facility property updated.");
    } catch (error) {
      message.error(error?.message || "Failed to update care facility property.");
    } finally {
      setSaving(false);
      setUpdatingPropKey("");
    }
  };

  const updateSelectedContactProperty = (key, label, value) =>
    openSelectedPropEditor({
      title: label,
      propName: "contactInformation",
      value,
      buildValue: (next) => ({
        ...(selected?.contactInformation || {}),
        [key]: trim(next) || null,
      }),
    });

  const updateSelectedLocationProperty = (key, label, value) =>
    openSelectedPropEditor({
      title: label,
      propName: "locationInformation",
      value,
      buildValue: (next) => ({
        ...(selected?.locationInformation || {}),
        [key]: trim(next) || null,
      }),
    });

  const uploadSelectedFile = async (field, file) => {
    if (!selected?.id) return;
    try {
      setUpdatingPropKey(field);
      const result = await uploadFile(file, actorId, {
        purpose: field === "logo" || field === "profilePhoto" ? "PROFILE_PHOTO" : "OTHER",
        preserveOriginal: field === "certificate" || field === "otherDocs",
        confirmUpload: false,
      });
      if (!result?.fileId) throw new Error("Upload failed: missing file ID.");
      if (field === "otherDocs") {
        const existingIds = (selected.otherDocs || []).map(fileIdValue).filter(Boolean);
        await patchSelectedProp("otherDocs", [...existingIds, result.fileId]);
      } else {
        await patchSelectedProp(field, result.fileId);
      }
      message.success("Care facility file updated.");
    } catch (error) {
      message.error(error?.message || "Failed to update care facility file.");
    } finally {
      setUpdatingPropKey("");
    }
  };

  const clearSelectedFile = async (field) => {
    try {
      setUpdatingPropKey(field);
      await patchSelectedProp(field, field === "otherDocs" ? [] : null);
      message.success("Care facility file removed.");
    } catch (error) {
      message.error(error?.message || "Failed to remove care facility file.");
    } finally {
      setUpdatingPropKey("");
    }
  };

  const removeSelectedOtherDoc = async (indexToRemove) => {
    try {
      setUpdatingPropKey("otherDocs");
      const nextIds = (selected?.otherDocs || [])
        .filter((_, index) => index !== indexToRemove)
        .map(fileIdValue)
        .filter(Boolean);
      await patchSelectedProp("otherDocs", nextIds);
      message.success("Document removed.");
    } catch (error) {
      message.error(error?.message || "Failed to remove document.");
    } finally {
      setUpdatingPropKey("");
    }
  };

  const uploadButtonProps = (field) => ({
    showUploadList: false,
    customRequest: ({ file, onSuccess, onError }) => {
      uploadSelectedFile(field, file)
        .then(() => onSuccess?.("ok"))
        .catch((error) => onError?.(error));
    },
  });

  const syncOrganizationOfferings = async (organizationId, nextServiceIds = []) => {
    if (!organizationId) return;
    const current = await olreadiaService.serviceOfferings.organizationServices
      .list(organizationId)
      .catch(() => []);
    const currentIds = new Set(pageContent(current).map((service) => service.id).filter(Boolean));
    const selectedIds = new Set((nextServiceIds || []).filter(Boolean));
    const toAdd = [...selectedIds].filter((serviceId) => !currentIds.has(serviceId));
    const toRemove = [...currentIds].filter((serviceId) => !selectedIds.has(serviceId));

    await Promise.all([
      ...toAdd.map((serviceId) =>
        olreadiaService.serviceOfferings.organizationServices.add(organizationId, serviceId, actorId),
      ),
      ...toRemove.map((serviceId) =>
        olreadiaService.serviceOfferings.organizationServices.remove(organizationId, serviceId, actorId),
      ),
    ]);
  };

  const syncLocationOfferings = async (organizationId, locationId, nextServiceIds = []) => {
    if (!organizationId || !locationId) return;
    const parentCurrent = await olreadiaService.serviceOfferings.organizationServices
      .list(organizationId)
      .catch(() => []);
    const parentIds = new Set(pageContent(parentCurrent).map((service) => service.id).filter(Boolean));
    const selectedIds = new Set((nextServiceIds || []).filter(Boolean));
    const missingParentIds = [...selectedIds].filter((serviceId) => !parentIds.has(serviceId));
    if (missingParentIds.length) {
      await Promise.all(
        missingParentIds.map((serviceId) =>
          olreadiaService.serviceOfferings.organizationServices.add(organizationId, serviceId, actorId),
        ),
      );
    }
    const current = await olreadiaService.serviceOfferings.businessLocationServices
      .list(organizationId, locationId)
      .catch(() => []);
    const currentIds = new Set(pageContent(current).map((service) => service.id).filter(Boolean));
    const toAdd = [...selectedIds].filter((serviceId) => !currentIds.has(serviceId));
    const toRemove = [...currentIds].filter((serviceId) => !selectedIds.has(serviceId));

    await Promise.all([
      ...toAdd.map((serviceId) =>
        olreadiaService.serviceOfferings.businessLocationServices.add(
          organizationId,
          locationId,
          serviceId,
          actorId,
        ),
      ),
      ...toRemove.map((serviceId) =>
        olreadiaService.serviceOfferings.businessLocationServices.remove(
          organizationId,
          locationId,
          serviceId,
          actorId,
        ),
      ),
    ]);
  };

  const submitOrganization = async (values) => {
    try {
      setSaving(true);
      const payload = organizationPayload(values, editor.organization || {});
      let entity;
      let targetOrganizationId = "";
      if (editor?.mode === "edit" && editor.organization?.id) {
        targetOrganizationId = editor.organization.id;
        entity = await patchOrganizationProps(targetOrganizationId, payload, actorId);
        message.success("Care facility updated.");
      } else {
        entity = await olreadiaService.organizations.create(payload, actorId);
        targetOrganizationId = entity?.id || "";
        message.success("Care facility created.");
      }
      if (targetOrganizationId) {
        await syncOrganizationOfferings(targetOrganizationId, values.serviceIds);
      }
      closeEditor();
      await loadOrganizations();
      if (targetOrganizationId) await loadSelected(targetOrganizationId, true);
    } catch (error) {
      message.error(error?.message || "Failed to save care facility.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteOrganization = (entity) => {
    Modal.confirm({
      title: "Delete care facility?",
      content: `This will remove ${organizationName(entity)} and its saved care facility data.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        await olreadiaService.organizations.delete(entity.id, actorId);
        message.success("Care facility deleted.");
        if (selected?.id === entity.id) {
          setSelected(null);
          setDetailOpen(false);
        }
        await loadOrganizations();
      },
    });
  };

  const openLocationCreate = () => {
    if (!selected?.id) return;
    setLocationEditor({ mode: "create", location: null });
    locationForm.setFieldsValue({
      ...locationInitialValues(),
      primaryEmail: selected.contactInformation?.primaryEmail || "",
      primaryPhone: selected.contactInformation?.primaryPhone || "",
      websiteUrl: selected.contactInformation?.websiteUrl || "",
      contactPersonName: selected.contactInformation?.contactPersonName || "",
      contactPersonTitle: selected.contactInformation?.contactPersonTitle || "",
    });
  };

  const openLocationEdit = (location) => {
    setLocationEditor({ mode: "edit", location });
    locationForm.setFieldsValue(locationInitialValues(location));
  };

  const closeLocationEditor = () => {
    setLocationEditor(null);
    locationForm.resetFields();
  };

  const submitLocation = async (values) => {
    if (!selected?.id) return;
    try {
      setSaving(true);
      const payload = locationPayload(values);
      if (locationEditor?.mode === "edit" && locationEditor.location?.id) {
        const updated = await olreadiaService.organizations.locations.update(
          selected.id,
          locationEditor.location.id,
          payload,
          actorId,
        );
        await syncLocationOfferings(selected.id, updated?.id || locationEditor.location.id, values.serviceIds);
        message.success("Care facility location updated.");
      } else {
        const created = await olreadiaService.organizations.locations.create(selected.id, payload, actorId);
        await syncLocationOfferings(selected.id, created?.id, values.serviceIds);
        message.success("Care facility location added.");
      }
      closeLocationEditor();
      await loadSelected(selected.id, false);
      await loadOrganizations();
    } catch (error) {
      message.error(error?.message || "Failed to save care facility location.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteLocation = (location) => {
    Modal.confirm({
      title: "Delete care facility location?",
      content: `This will remove ${location.name || "this location"} from ${organizationName(selected)}.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        await olreadiaService.organizations.locations.delete(selected.id, location.id, actorId);
        message.success("Care facility location deleted.");
        await loadSelected(selected.id, false);
        await loadOrganizations();
      },
    });
  };

  const openUserEditor = () => {
    userForm.setFieldsValue({
      email: "",
      role: "MEMBER",
      status: "INVITED",
      primaryContact: false,
    });
    setUserEditorOpen(true);
  };

  const submitUser = async (values) => {
    if (!selected?.id) return;
    try {
      setSaving(true);
      await olreadiaService.organizations.users.add(
        selected.id,
        {
          email: trim(values.email),
          role: values.role || "MEMBER",
          status: values.status || "INVITED",
          primaryContact: Boolean(values.primaryContact),
        },
        actorId,
      );
      message.success("Care facility user added.");
      setUserEditorOpen(false);
      userForm.resetFields();
      await loadSelected(selected.id, false);
    } catch (error) {
      message.error(error?.message || "Failed to add care facility user.");
    } finally {
      setSaving(false);
    }
  };

  const removeUser = (orgUser) => {
    Modal.confirm({
      title: "Remove care facility user?",
      content: `This will remove ${userLabel(orgUser)} from ${organizationName(selected)}.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        await olreadiaService.organizations.users.remove(selected.id, orgUser.id, actorId);
        message.success("Care facility user removed.");
        await loadSelected(selected.id, false);
      },
    });
  };

  const addOffering = async (service) => {
    if (!selected?.id || !service?.id) return;
    try {
      setActionKey(`add-service-${service.id}`);
      await olreadiaService.serviceOfferings.organizationServices.add(selected.id, service.id, actorId);
      message.success("Service offering added.");
      setOfferingQuery("");
      await loadSelected(selected.id, false);
    } catch (error) {
      message.error(error?.message || "Failed to add service offering.");
    } finally {
      setActionKey("");
    }
  };

  const removeOffering = async (service) => {
    if (!selected?.id || !service?.id) return;
    try {
      setActionKey(`remove-service-${service.id}`);
      await olreadiaService.serviceOfferings.organizationServices.remove(selected.id, service.id, actorId);
      message.success("Service offering removed.");
      await loadSelected(selected.id, false);
    } catch (error) {
      message.error(error?.message || "Failed to remove service offering.");
    } finally {
      setActionKey("");
    }
  };

  const total = pageTotal(organizationsPage, organizations.length);
  const organizationOverview = organizations.reduce(
    (summary, entity) => {
      const locations = entity.businessLocations || entity.organizationLocations || [];
      const users = entity.users || entity.organizationUsers || [];
      const services = entity.organizationOfferings || entity.offerings || entity.services || [];
      return {
        organizations: summary.organizations + 1,
        active: summary.active + (String(entity.status || "").toUpperCase() === "ACTIVE" ? 1 : 0),
        locations: summary.locations + locations.length,
        services: summary.services + services.length,
        users: summary.users + users.length,
      };
    },
    { organizations: 0, active: 0, locations: 0, services: 0, users: 0 }
  );

  return (
    <div className="project-page organizations-page">
      <div className="organizations-header">
        <div>
          <Text className="organizations-eyebrow">Care Facilities</Text>
          <Title level={3}>Care Facilities and Locations</Title>
          <Paragraph>
            Manage care facilities, users, service offerings, and physical locations linked to your account.
          </Paragraph>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={refreshAll}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
            style={{ background: OLIVEEB_COLORS.oliveebGreen, borderColor: OLIVEEB_COLORS.oliveebGreen }}
          >
            Add Care Facility
          </Button>
        </Space>
      </div>

      <div className="organizations-page-layout">
        <div className="organizations-page-main">
          <div className="organizations-command-panel">
            <div className="organizations-overview-strip">
              <div className="organizations-overview-item">
                <span><BankOutlined /></span>
                <div>
                  <Text>Care Facilities</Text>
                  <strong>{total || organizationOverview.organizations}</strong>
                </div>
              </div>
              <div className="organizations-overview-item">
                <span><EnvironmentOutlined /></span>
                <div>
                  <Text>Locations</Text>
                  <strong>{organizationOverview.locations}</strong>
                </div>
              </div>
              <div className="organizations-overview-item">
                <span><ShopOutlined /></span>
                <div>
                  <Text>Services</Text>
                  <strong>{organizationOverview.services}</strong>
                </div>
              </div>
              <div className="organizations-overview-item">
                <span><TeamOutlined /></span>
                <div>
                  <Text>Users</Text>
                  <strong>{organizationOverview.users}</strong>
                </div>
              </div>
            </div>
            <Text className="organizations-search-note">
              {organizationOverview.active} active care facilit{organizationOverview.active === 1 ? "y" : "ies"} in this view
            </Text>
          </div>

          {loading ? (
            <Card>
              <Skeleton active paragraph={{ rows: 8 }} />
            </Card>
          ) : organizations.length ? (
            <Space direction="vertical" size={12} className="organizations-list">
              {organizations.map((entity) => {
                const locations = entity.businessLocations || entity.organizationLocations || [];
                const users = entity.users || entity.organizationUsers || [];
                const services = entity.organizationOfferings || entity.offerings || entity.services || [];
                return (
                  <Card
                    key={entity.id}
                    className="organizations-card organizations-card--clickable"
                    onClick={() => loadSelected(entity.id, true)}
                  >
                    <div className="organizations-card-main">
                      <span className="organizations-avatar">{orgInitial(entity)}</span>
                      <div className="organizations-card-copy">
                        <div className="organizations-card-title-row">
                          <Title level={4}>{organizationName(entity)}</Title>
                          <Tag color={statusColor(entity.status)}>{readable(entity.status || "Draft")}</Tag>
                        </div>
                        <Text className="organizations-card-meta">
                          {locationTypeLabel(entity.businessLocationType || entity.organizationLocationType)}
                          {contactText(entity) ? ` • ${contactText(entity)}` : ""}
                        </Text>
                        <Text className="organizations-card-address">
                          {addressText(entity) || "Primary location not set"}
                        </Text>
                      </div>
                      <div className="organizations-card-stats">
                        <MiniStat label="Locations" value={locations.length} />
                        <MiniStat label="Users" value={users.length} />
                        <MiniStat label="Services" value={services.length} />
                      </div>
                      <Space className="organizations-card-actions" wrap>
                        <Button className="organizations-action-btn organizations-action-btn--view" icon={<EyeOutlined />} onClick={(event) => {
                          event.stopPropagation();
                          loadSelected(entity.id, true);
                        }}>
                          View
                        </Button>
                        <Button
                          className="organizations-action-btn organizations-action-btn--manage"
                          icon={<SettingOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(paths.organizationDetails(entity.id));
                          }}
                          style={{ borderColor: OLIVEEB_COLORS.oliveebBlue, color: OLIVEEB_COLORS.oliveebBlue }}
                        >
                          Manage
                        </Button>
                        <Button className="organizations-action-btn organizations-action-btn--delete" danger icon={<DeleteOutlined />} onClick={(event) => {
                          event.stopPropagation();
                          confirmDeleteOrganization(entity);
                        }}>
                          Delete
                        </Button>
                      </Space>
                    </div>
                  </Card>
                );
              })}
              <div className="organizations-pagination">
                <Pagination
                  current={page}
                  total={total}
                  pageSize={pageSize}
                  showSizeChanger
                  onChange={(nextPage, nextSize) => {
                    setPage(nextPage);
                    setPageSize(nextSize);
                  }}
                />
              </div>
            </Space>
          ) : (
            <Card>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No care facilities found"
              >
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Add Care Facility
                </Button>
              </Empty>
            </Card>
          )}
        </div>

        <SidePromotionSection className="organizations-page-promotions" />
      </div>

      <Drawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Care Facility details"
        width={720}
        className="organizations-drawer"
        destroyOnClose
      >
        {detailLoading && !selected ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : selected ? (
          <Space direction="vertical" size={14} className="organizations-drawer-stack">
            <div className="organizations-detail-hero">
              <span className="organizations-avatar is-large">{orgInitial(selected)}</span>
              <div>
                <Title level={4}>{organizationName(selected)}</Title>
                <Space wrap size={[6, 6]}>
                  <Tag color={statusColor(selected.status)}>{readable(selected.status || "Draft")}</Tag>
                  <Tag color="blue">
                    {locationTypeLabel(selected.businessLocationType || selected.organizationLocationType)}
                  </Tag>
                </Space>
              </div>
            </div>

            <div className="organizations-detail-grid">
              <DetailRow label="Name" value={organizationName(selected)} />
              <DetailRow label="Description" value={selected.description} />
              <DetailRow label="Type" value={locationTypeLabel(selected.businessLocationType || selected.organizationLocationType)} />
              <DetailRow label="Status" value={readable(selected.status || "Draft")} />
              <DetailRow label="Primary email" value={selected.contactInformation?.primaryEmail || selected.email} />
              <DetailRow label="Secondary email" value={selected.contactInformation?.secondaryEmail} />
              <DetailRow label="Primary phone" value={selected.contactInformation?.primaryPhone || selected.phone} />
              <DetailRow label="Secondary phone" value={selected.contactInformation?.secondaryPhone} />
              <DetailRow label="Website" value={selected.contactInformation?.websiteUrl || selected.websiteUrl} />
              <DetailRow label="Contact" value={selected.contactInformation?.contactPersonName} />
              <DetailRow label="Contact title" value={selected.contactInformation?.contactPersonTitle} />
              <DetailRow label="EIN" value={selected.ein} />
              <DetailRow label="Created" value={formatDate(selected.createdAt)} />
              <DetailRow label="Address line 1" value={selected.locationInformation?.addressLine1} />
              <DetailRow label="Address line 2" value={selected.locationInformation?.addressLine2} />
              <DetailRow label="City" value={selected.locationInformation?.city} />
              <DetailRow label="County" value={selected.locationInformation?.county} />
              <DetailRow label="State" value={selected.locationInformation?.state} />
              <DetailRow label="Zip code" value={selected.locationInformation?.zipCode} />
              <DetailRow label="Country" value={selected.locationInformation?.country} />
            </div>

            <Card
              size="small"
              title={<span className="organizations-section-title"><FileImageOutlined /> Files</span>}
              className="organizations-files-card"
            >
              <Space direction="vertical" size={8} className="organizations-inner-list">
                {["certificate", "logo", "profilePhoto"].map((field) => {
                  const labels = { certificate: "Certificate", logo: "Logo", profilePhoto: "Profile photo" };
                  const existing = selectedFileItems.find((item) => item.key === field);
                  return (
                    <div key={field} className="organizations-file-manage-row">
                      {existing ? (
                        <FileThumb item={existing} url={existing.url} loading={false} onPreview={() => openFilePreview(existing)} />
                      ) : (
                        <div className="organizations-file-empty">
                          <FileOutlined />
                          <Text>{labels[field]}</Text>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="organizations-file-manage-row">
                  <div>
                    <Text strong>Other documents</Text>
                    <Text type="secondary">{selectedOtherDocs.length ? `${selectedOtherDocs.length} document${selectedOtherDocs.length === 1 ? "" : "s"}` : "No documents added yet."}</Text>
                  </div>
                </div>
                {selectedOtherDocs.length ? (
                  <div className="organizations-file-grid">
                    {selectedOtherDocs.map((doc, index) => {
                      const item = { key: `other-${index}`, label: `Document ${index + 1}`, value: doc, kind: "document", fileId: fileIdValue(doc), url: fileUrlValue(doc) };
                      const key = item.fileId || item.url || index;
                      return (
                        <div key={`${item.key}-${key}`} className="organizations-document-chip">
                          <FileThumb item={item} url={item.url} loading={false} onPreview={() => openFilePreview(item)} />
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </Space>
            </Card>

            <Card
              size="small"
              title={<span className="organizations-section-title"><ShopOutlined /> Service offerings</span>}
            >
              <Space direction="vertical" size={10} className="organizations-inner-list">
                {selectedServices.length ? (
                  <Space wrap>
                    {selectedServices.map((service) => (
                      <Tag
                        key={service.id || service.code || service.name}
                      >
                        {serviceLabel(service)}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">No service offerings added.</Text>
                )}
              </Space>
            </Card>

            <Card
              size="small"
              title={<span className="organizations-section-title"><TeamOutlined /> Care Facility users</span>}
            >
              {selectedUsers.length ? (
                <Space direction="vertical" size={8} className="organizations-inner-list">
                  {selectedUsers.map((orgUser, index) => (
                    <div key={orgUser.id || userEmail(orgUser) || index} className="organizations-line-item">
                      <div>
                        <Text strong>{userLabel(orgUser)}</Text>
                        <Text type="secondary">{userEmail(orgUser) || "Email not set"}</Text>
                      </div>
                      <Space wrap>
                        <Tag>{readable(orgUser.role || "Member")}</Tag>
                        <Tag color={statusColor(orgUser.status)}>{readable(orgUser.status || "Active")}</Tag>
                      </Space>
                    </div>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No care facility users added yet.</Text>
              )}
            </Card>

            <Card
              size="small"
              title={<span className="organizations-section-title"><BankOutlined /> Care Facility locations</span>}
            >
              {selectedLocations.length ? (
                <Space direction="vertical" size={8} className="organizations-inner-list">
                  {selectedLocations.map((location) => (
                    <div
                      key={location.id || location.name}
                      className="organizations-line-item organizations-line-item--clickable"
                      onClick={() => setLocationDetails(location)}
                    >
                      <div>
                        <Text strong>{location.name || "Care facility location"}</Text>
                        <Text type="secondary">
                          {locationTypeLabel(location.businessLocationType || location.organizationLocationType)}
                          {location.status ? ` • ${readable(location.status)}` : ""}
                        </Text>
                        <Text type="secondary">{addressText(location) || "Address not set"}</Text>
                        {contactText(location) ? (
                          <Text type="secondary">{contactText(location)}</Text>
                        ) : null}
                        {location.contactInformation?.contactPersonTitle ? (
                          <Text type="secondary">{location.contactInformation.contactPersonTitle}</Text>
                        ) : null}
                        {(location.services || location.organizationOfferings || []).length ? (
                          <Space wrap size={[4, 4]} className="organizations-line-services">
                            {(location.services || location.organizationOfferings || []).map((service) => (
                              <Tag key={service.id || service.code || service.name}>{serviceLabel(service)}</Tag>
                            ))}
                          </Space>
                        ) : null}
                      </div>
                      <Space>
                        <Button size="small" icon={<EyeOutlined />} onClick={(event) => {
                          event.stopPropagation();
                          setLocationDetails(location);
                        }}>
                          View
                        </Button>
                      </Space>
                    </div>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No physical locations added yet.</Text>
              )}
            </Card>
          </Space>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a care facility to review." />
        )}
      </Drawer>

      <Drawer
        open={Boolean(propEditor)}
        onClose={() => setPropEditor(null)}
        title={propEditor?.title || "Update property"}
        width={420}
        className="organizations-drawer organizations-property-drawer"
        destroyOnClose
      >
        {propEditor ? (
          <Space direction="vertical" size={12} className="organizations-drawer-stack">
            <Text type="secondary">Update this care facility property only.</Text>
            {propEditor.options ? (
              <Select
                value={propEditor.value}
                options={propEditor.options}
                showSearch
                optionFilterProp="label"
                onChange={(value) => setPropEditor((current) => ({ ...current, value }))}
              />
            ) : propEditor.multiline ? (
              <TextArea
                rows={5}
                value={propEditor.value}
                onChange={(event) => setPropEditor((current) => ({ ...current, value: event.target.value }))}
              />
            ) : (
              <Input
                value={propEditor.value}
                onChange={(event) => setPropEditor((current) => ({ ...current, value: event.target.value }))}
              />
            )}
            <div className="organizations-drawer-actions">
              <Button danger onClick={() => setPropEditor(null)}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={saveSelectedProp}>
                Done
              </Button>
            </div>
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        open={Boolean(locationDetails)}
        onClose={() => setLocationDetails(null)}
        title="Care Facility location"
        width={560}
        className="organizations-drawer organizations-location-drawer"
        destroyOnClose
      >
        {locationDetails ? (
          <Space direction="vertical" size={12} className="organizations-drawer-stack">
            <div className="organizations-location-hero">
              <span className="organizations-location-hero__icon"><EnvironmentOutlined /></span>
              <div className="organizations-location-hero__copy">
                <Title level={4}>{locationDetails.name || "Care facility location"}</Title>
                <Text>{organizationName(selected)}</Text>
                <Space wrap size={[5, 5]} className="organizations-location-badges">
                  <Tag color={statusColor(locationDetails.status)}>{readable(locationDetails.status || "Active")}</Tag>
                  <Tag color="blue">
                    {locationTypeLabel(locationDetails.businessLocationType || locationDetails.organizationLocationType || locationDetails.type)}
                  </Tag>
                  <Tag color="green">{locationDetailsServices.length} services</Tag>
                </Space>
              </div>
            </div>

            {locationDetails.description ? (
              <Card size="small" className="organizations-location-note">
                <Paragraph>{locationDetails.description}</Paragraph>
              </Card>
            ) : null}

            <Card size="small" title={<span className="organizations-section-title"><MailOutlined /> Contact information</span>}>
              <div className="organizations-detail-grid">
                <DetailRow label="Primary email" value={locationDetails.contactInformation?.primaryEmail} />
                <DetailRow label="Secondary email" value={locationDetails.contactInformation?.secondaryEmail} />
                <DetailRow label="Primary phone" value={locationDetails.contactInformation?.primaryPhone} />
                <DetailRow label="Secondary phone" value={locationDetails.contactInformation?.secondaryPhone} />
                <DetailRow label="Website" value={locationDetails.contactInformation?.websiteUrl} />
                <DetailRow label="Contact person" value={locationDetails.contactInformation?.contactPersonName} />
                <DetailRow label="Contact title" value={locationDetails.contactInformation?.contactPersonTitle} />
              </div>
            </Card>

            <Card size="small" title={<span className="organizations-section-title"><EnvironmentOutlined /> Location information</span>}>
              <div className="organizations-detail-grid">
                <DetailRow label="Address" value={addressText(locationDetails)} />
              </div>
              <Button
                className="organizations-map-button"
                icon={<GlobalOutlined />}
                href={mapUrl(locationDetails)}
                target="_blank"
                rel="noreferrer"
                disabled={!addressText(locationDetails)}
              >
                Open Map
              </Button>
            </Card>

            <Card size="small" title={<span className="organizations-section-title"><ShopOutlined /> Service offerings</span>}>
              {locationDetailsServices.length ? (
                <div className="organizations-service-card-list">
                  {locationDetailsServices.map((service, index) => (
                    <div key={service.id || service.code || service.name || index} className="organizations-service-card">
                      <span className="organizations-service-card__mark">✓</span>
                      <div>
                        <Text strong>{serviceLabel(service)}</Text>
                        {service.category ? <Text type="secondary">{readable(service.category)}</Text> : null}
                        {service.description ? <Text type="secondary">{service.description}</Text> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Text type="secondary">No service offerings selected.</Text>
              )}
            </Card>
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        open={Boolean(editor)}
        onClose={closeEditor}
        title={editor?.mode === "edit" ? "Edit Care Facility" : "Add Care Facility"}
        width={620}
        className="organizations-drawer"
        destroyOnClose
      >
        <OrganizationForm
          form={form}
          locationTypeOptions={locationTypeOptions}
          serviceOptions={serviceOptions}
          saving={saving}
          editing={editor?.mode === "edit"}
          onCancel={closeEditor}
          onSubmit={submitOrganization}
        />
      </Drawer>

      <Drawer
        open={Boolean(locationEditor)}
        onClose={closeLocationEditor}
        title={locationEditor?.mode === "edit" ? "Edit care facility location" : "Add care facility location"}
        width={560}
        className="organizations-drawer"
        destroyOnClose
      >
        <Form form={locationForm} layout="vertical" requiredMark={false} onFinish={submitLocation}>
          <div className="organizations-form-section">
            <Text className="organizations-section-title">Care Facility location details</Text>
            <Form.Item
              name="name"
              label="Location name"
              rules={[{ required: true, message: "Enter location name." }]}
            >
              <Input placeholder="Facility, office, branch, or home" />
            </Form.Item>
            <Form.Item
              name="organizationLocationTypeId"
              label="Location type"
              rules={[{ required: true, message: "Select location type." }]}
            >
              <Select showSearch optionFilterProp="label" options={locationTypeOptions} />
            </Form.Item>
            <Form.Item name="status" label="Status">
              <Select options={USER_STATUS_OPTIONS.map((value) => ({ value, label: readable(value) }))} />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <TextArea rows={2} />
            </Form.Item>
          </div>

          <div className="organizations-form-section">
            <Text className="organizations-section-title">Care Facility service offerings</Text>
            <Form.Item name="serviceIds" label="Services offered at this location">
              <Select
                mode="multiple"
                allowClear
                showSearch
                placeholder="Select location service offerings"
                optionFilterProp="label"
                options={locationServiceOptions}
              />
            </Form.Item>
          </div>

          <div className="organizations-form-section">
            <Text className="organizations-section-title">Location contact</Text>
            <Space.Compact block>
              <Form.Item name="primaryEmail" label="Primary email" className="organizations-compact-item">
                <Input placeholder="location@example.com" />
              </Form.Item>
              <Form.Item name="secondaryEmail" label="Secondary email" className="organizations-compact-item">
                <Input placeholder="Optional email" />
              </Form.Item>
            </Space.Compact>
            <Space.Compact block>
              <Form.Item name="primaryPhone" label="Primary phone" className="organizations-compact-item">
                <Input placeholder="+1..." />
              </Form.Item>
              <Form.Item name="secondaryPhone" label="Secondary phone" className="organizations-compact-item">
                <Input placeholder="Optional phone" />
              </Form.Item>
            </Space.Compact>
            <Space.Compact block>
              <Form.Item name="contactPersonName" label="Contact person" className="organizations-compact-item">
                <Input placeholder="Primary contact" />
              </Form.Item>
              <Form.Item name="contactPersonTitle" label="Contact title" className="organizations-compact-item">
                <Input placeholder="Manager, owner, admin" />
              </Form.Item>
            </Space.Compact>
            <Form.Item name="websiteUrl" label="Website">
              <Input placeholder="https://..." />
            </Form.Item>
          </div>

          <div className="organizations-form-section">
            <Text className="organizations-section-title">Location address</Text>
            <AddressAutocomplete googleMapsApiKey={googleMapsApiKey} />
            <Form.Item name="addressLine1" label="Address line 1">
              <Input />
            </Form.Item>
            <Form.Item name="addressLine2" label="Address line 2">
              <Input />
            </Form.Item>
            <Space.Compact block>
              <Form.Item name="city" label="City" className="organizations-compact-item">
                <Input />
              </Form.Item>
              <Form.Item name="county" label="County" className="organizations-compact-item">
                <Input />
              </Form.Item>
            </Space.Compact>
            <Space.Compact block>
              <Form.Item name="state" label="State" className="organizations-compact-item">
                <Input />
              </Form.Item>
              <Form.Item name="zipCode" label="Zip code" className="organizations-compact-item">
                <Input />
              </Form.Item>
            </Space.Compact>
            <Space.Compact block>
              <Form.Item name="country" label="Country" className="organizations-compact-item">
                <Input />
              </Form.Item>
            </Space.Compact>
          </div>
          <div className="organizations-drawer-actions">
            <Button onClick={closeLocationEditor}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save Location
            </Button>
          </div>
        </Form>
      </Drawer>

      <Drawer
        open={userEditorOpen}
        onClose={() => setUserEditorOpen(false)}
        title="Add care facility user"
        width={460}
        className="organizations-drawer"
        destroyOnClose
      >
        <Alert
          showIcon
          type="info"
          message="Invite or add a user who can help manage this care facility."
          className="organizations-user-alert"
        />
        <Form form={userForm} layout="vertical" requiredMark={false} onFinish={submitUser}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Enter user email." },
              { type: "email", message: "Enter a valid email." },
            ]}
          >
            <Input placeholder="new.user@example.com" />
          </Form.Item>
          <Form.Item name="role" label="Role">
            <Select options={USER_ROLE_OPTIONS.map((value) => ({ value, label: readable(value) }))} />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={USER_STATUS_OPTIONS.map((value) => ({ value, label: readable(value) }))} />
          </Form.Item>
          <Form.Item name="primaryContact" label="Primary contact">
            <Select
              options={[
                { value: false, label: "No" },
                { value: true, label: "Yes" },
              ]}
            />
          </Form.Item>
          <div className="organizations-drawer-actions">
            <Button onClick={() => setUserEditorOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Add User
            </Button>
          </div>
        </Form>
      </Drawer>

      <Modal
        open={Boolean(filePreview)}
        onCancel={() => setFilePreview(null)}
        footer={
          filePreview?.url ? (
            <Button type="primary" href={filePreview.url} target="_blank" rel="noreferrer">
              Open in new tab
            </Button>
          ) : null
        }
        title={filePreview?.label || "File preview"}
        width={760}
        className="organizations-file-preview-modal"
        destroyOnClose
      >
        {filePreview?.url ? (
          <div className="organizations-file-preview">
            <img
              src={filePreview.url}
              alt={filePreview.label || "File preview"}
              onError={(event) => {
                event.currentTarget.style.display = "none";
                const fallback = event.currentTarget.nextElementSibling;
                if (fallback) fallback.style.display = "grid";
              }}
            />
            <div className="organizations-file-preview__fallback">
              <FileOutlined />
              <Text>This file cannot be shown as an image preview.</Text>
              <Button href={filePreview.url} target="_blank" rel="noreferrer">
                Open file
              </Button>
            </div>
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Preview is not available for this file." />
        )}
      </Modal>
    </div>
  );
}
